import { useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import {
  investigatorPlace,
  useCatalogSearch,
  type CatalogInvestigator,
  type InvestigatorSearch,
} from './investigator-picker';

export interface TeamMemberDirectory {
  tenantName: string;
  members: { userId: string; name: string; username: string; email: string }[];
  loading: boolean;
  error: boolean;
  retry: () => void;
  add: (userId: string) => Promise<unknown>;
}

/** Catalog PIs surface in the same search so a PI can join before signing up. */
export interface TeamMemberCatalog {
  search: InvestigatorSearch;
  /** Preassign the PI role to the catalog email and link the identity. */
  add: (pi: CatalogInvestigator) => Promise<unknown>;
  /** Open the create form with the PI role and this identity preselected. */
  create: (pi: CatalogInvestigator) => void;
}

function failureDetail(failure: unknown, fallback: string): string {
  const detail = (failure as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

export function TeamMemberSearch({
  directory,
  catalog,
  memberIds,
  disabled,
  onAdded,
  onCreate,
}: {
  directory: TeamMemberDirectory;
  catalog?: TeamMemberCatalog;
  memberIds: string[];
  disabled: boolean;
  onAdded: (userId: string) => void;
  onCreate: (email: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const matches = directory.members.filter(member => {
    const text = `${member.name} ${member.username} ${member.email}`.toLocaleLowerCase();
    return terms.every(term => text.includes(term));
  });
  const exactEmail = directory.members.some(
    member => member.email.toLowerCase() === query.trim().toLowerCase()
  );
  const lookup = useCatalogSearch(catalog?.search, query);
  const catalogRows = lookup.results.filter(
    pi => !pi.registeredUserId || !matches.some(member => member.userId === pi.registeredUserId)
  );

  const addRegistered = async (userId: string) => {
    setAdding(userId);
    setError(null);
    try {
      await directory.add(userId);
      onAdded(userId);
      setQuery('');
    } catch (failure) {
      setError(failureDetail(failure, 'Could not add this member. Please try again.'));
    } finally {
      setAdding(null);
    }
  };

  const addCatalog = async (pi: CatalogInvestigator) => {
    if (!catalog) return;
    setAdding(pi.slug);
    setError(null);
    try {
      await catalog.add(pi);
      setQuery('');
    } catch (failure) {
      setError(failureDetail(failure, 'Could not add this investigator. Please try again.'));
    } finally {
      setAdding(null);
    }
  };

  return (
    <section aria-label="Find team members" className="space-y-3 rounded-3xl border p-4">
      <label htmlFor="team-member-search" className="text-sm font-medium">
        Find members in {directory.tenantName}
      </label>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground"
        />
        <Input
          id="team-member-search"
          type="search"
          className="pl-9"
          placeholder="Search by name or email"
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setError(null);
          }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {catalog
          ? 'Search people who have signed up for this tenant and principal investigators in the catalog, or create a member before signup.'
          : 'Search people who have signed up for this tenant, or create a member before signup.'}
      </p>
      {directory.loading && (
        <p role="status" className="text-sm">
          Loading tenant members…
        </p>
      )}
      {directory.error && (
        <p role="alert" className="text-sm">
          Could not load tenant members.{' '}
          <Button variant="ghost" size="sm" onClick={directory.retry}>
            Retry member search
          </Button>
        </p>
      )}
      {!directory.loading && !directory.error && terms.length > 0 && (
        <div className="space-y-2" aria-label="Search results">
          {matches.length === 0 && !lookup.active && (
            <p role="status" className="text-sm text-muted-foreground">
              No members found in {directory.tenantName}.
            </p>
          )}
          {matches.slice(0, 20).map(member => (
            <div
              key={member.userId}
              className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-muted/50 p-3"
            >
              <div className="min-w-0 text-sm">
                <p className="font-medium">{member.name}</p>
                <p className="break-all text-muted-foreground">{member.email || member.username}</p>
              </div>
              {memberIds.includes(member.userId) ? (
                <span className="text-xs text-muted-foreground">Already in this team</span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={disabled || adding !== null}
                  aria-label={`Add ${member.name} to team`}
                  onClick={() => void addRegistered(member.userId)}
                >
                  {adding === member.userId ? 'Adding…' : 'Add to team'}
                </Button>
              )}
            </div>
          ))}
          {matches.length > 20 && (
            <p className="text-xs text-muted-foreground">
              Showing 20 of {matches.length} matches. Refine your search to see fewer results.
            </p>
          )}
          {matches.some(member => !memberIds.includes(member.userId)) && (
            <p className="text-xs text-muted-foreground">
              Adding someone moves them from their previous team in this tenant. You can assign
              roles after adding them.
            </p>
          )}
          {lookup.active && (
            <div className="space-y-2 pt-2" aria-label="Catalog investigators">
              <p className="text-xs font-medium">Principal investigators in the catalog</p>
              {lookup.loading && (
                <p role="status" className="text-sm text-muted-foreground">
                  Searching the catalog…
                </p>
              )}
              {lookup.error && (
                <p role="alert" className="text-sm">
                  Could not search the investigator catalog.
                </p>
              )}
              {!lookup.loading && !lookup.error && catalogRows.length === 0 && (
                <p role="status" className="text-sm text-muted-foreground">
                  No catalog investigators match this name.
                </p>
              )}
              {catalogRows.map(pi => (
                <CatalogRow
                  key={pi.slug}
                  pi={pi}
                  tenantName={directory.tenantName}
                  inTeam={!!pi.registeredUserId && memberIds.includes(pi.registeredUserId)}
                  busy={disabled || adding !== null}
                  adding={adding === pi.slug || adding === pi.registeredUserId}
                  onAddRegistered={userId => void addRegistered(userId)}
                  onAddCatalog={() => void addCatalog(pi)}
                  onCreate={() => catalog?.create(pi)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || directory.loading || directory.error || exactEmail}
        onClick={() => onCreate(query.includes('@') ? query.trim() : '')}
      >
        <UserPlus aria-hidden="true" className="mr-2 h-4 w-4" />
        Create member
      </Button>
    </section>
  );
}

function CatalogRow({
  pi,
  tenantName,
  inTeam,
  busy,
  adding,
  onAddRegistered,
  onAddCatalog,
  onCreate,
}: {
  pi: CatalogInvestigator;
  tenantName: string;
  inTeam: boolean;
  busy: boolean;
  adding: boolean;
  onAddRegistered: (userId: string) => void;
  onAddCatalog: () => void;
  onCreate: () => void;
}) {
  const place = investigatorPlace(pi);
  const studies = pi.studyCount ?? 0;
  let action: React.ReactNode;
  if (pi.status === 'member' || inTeam) {
    action = <span className="text-xs text-muted-foreground">Already in this team</span>;
  } else if (pi.status === 'pending') {
    action = (
      <span className="text-xs text-muted-foreground">Preassigned to this team before signup</span>
    );
  } else if (pi.status === 'catalog_only') {
    action = (
      <span className="text-xs text-muted-foreground">Linked to this team without an account</span>
    );
  } else if (pi.status === 'other_team') {
    action = <span className="text-xs text-muted-foreground">In another team of this tenant</span>;
  } else if (pi.registeredUserId) {
    action = (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        aria-label={`Add ${pi.fullName} to team`}
        onClick={() => onAddRegistered(pi.registeredUserId as string)}
      >
        {adding ? 'Adding…' : 'Add to team'}
      </Button>
    );
  } else if (pi.email && pi.emailDomainAllowed) {
    action = (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        aria-label={`Add ${pi.fullName} to team`}
        onClick={onAddCatalog}
      >
        {adding ? 'Adding…' : 'Add to team'}
      </Button>
    );
  } else {
    action = (
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        aria-label={`Create ${pi.fullName} as a member`}
        onClick={onCreate}
      >
        Create member
      </Button>
    );
  }
  const emailNote = !pi.email
    ? 'No email in the catalog. Enter their work email to prepare the membership.'
    : pi.emailDomainAllowed
      ? pi.email
      : `${pi.email} is outside the domains of ${tenantName}. Enter their work email to prepare the membership.`;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-muted/50 p-3">
      <div className="min-w-0 text-sm">
        <p className="font-medium">
          {pi.fullName}{' '}
          <span className="text-xs font-normal text-muted-foreground">
            Principal investigator · {studies} {studies === 1 ? 'study' : 'studies'}
          </span>
        </p>
        {place && <p className="text-muted-foreground">{place}</p>}
        <p className="break-all text-xs text-muted-foreground">{emailNote}</p>
      </div>
      {action}
    </div>
  );
}
